/**
 * ─── Point of Sale (POS) System — Schema ─────────────────────
 * General retail POS with product catalog, barcode scanning,
 * sales transactions, cash register shifts, and inventory tracking.
 *
 * Entities:
 *  - POSCategory  — Product category taxonomy
 *  - POSProduct   — Product catalog with SKU, barcode, pricing
 *  - POSSale      — Sale transaction (aggregate root)
 *  - POSSaleItem  — Line items in a sale
 *  - POSShift     — Cash register shift management
 */

import { z } from 'zod'
import {
  notesSchema,
  createQuerySchema,
  createUpdateSchema,
} from '@repo/core'

// ═══════════════════════════════════════════════════════════════
// POSCategory
// ═══════════════════════════════════════════════════════════════

export interface POSCategory {
  id: string
  tenantId: string
  branchId?: string
  name: string
  slug: string
  description?: string
  parentId?: string
  sortOrder: number
  color?: string
  icon?: string
  status: 'active' | 'inactive'
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const CreatePOSCategorySchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  name: z.string().min(1).max(100),
  slug: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  parentId: z.string().optional(),
  sortOrder: z.number().int().min(0).default(0),
  color: z.string().max(20).optional(),
  icon: z.string().max(50).optional(),
  status: z.enum(['active', 'inactive']).default('active'),
})

export const UpdatePOSCategorySchema = createUpdateSchema({
  name: z.string().min(1).max(100).optional(),
  slug: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  parentId: z.string().nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  color: z.string().max(20).optional(),
  icon: z.string().max(50).optional(),
  status: z.enum(['active', 'inactive']).optional(),
})

export const POSCategoryQuerySchema = createQuerySchema({
  status: z.enum(['active', 'inactive']).optional(),
  parentId: z.string().optional(),
})

// ═══════════════════════════════════════════════════════════════
// POSProduct
// ═══════════════════════════════════════════════════════════════

export type ProductUnit = 'piece' | 'kilogram' | 'gram' | 'liter' | 'milliliter' | 'meter' | 'box' | 'pack' | 'set' | 'pair' | 'bottle' | 'can'
export type ProductStatus = 'active' | 'inactive' | 'discontinued'
export type TaxType = 'vat' | 'vat_exempt' | 'zero_rated'

export interface POSProduct {
  id: string
  tenantId: string
  branchId?: string
  /** Unique product code / SKU */
  sku: string
  /** UPC / EAN barcode */
  barcode?: string
  /** Display name */
  name: string
  /** Short description for POS display */
  description?: string
  /** Category reference */
  categoryId?: string
  /** Category name (denormalized for display) */
  categoryName?: string
  /** Selling unit */
  unit: ProductUnit
  /** Retail selling price (PHP) */
  retailPrice: number
  /** Wholesale / bulk price (PHP) — if applicable */
  wholesalePrice?: number
  /** Minimum wholesale quantity */
  wholesaleMinQty?: number
  /** Cost price for margin calculation (PHP) */
  costPrice: number
  /** Markup percentage (computed from cost vs retail) */
  markupPercent: number
  /** Tax classification */
  taxType: TaxType
  /** VAT rate (e.g. 0.12 = 12%) */
  vatRate: number
  /** Price includes VAT */
  isVatInclusive: boolean
  /** Current stock quantity */
  stockOnHand: number
  /** Low stock alert threshold */
  minStockLevel: number
  /** Maximum desirable stock */
  maxStockLevel: number
  /** Supplier reference */
  supplierId?: string
  supplierName?: string
  /** Product image URL */
  imageUrl?: string
  /** Whether this product can be sold via POS register */
  isSellable: boolean
  /** Whether discount is allowed on this product */
  allowDiscount: boolean
  /** Sort order in catalog display */
  sortOrder: number
  status: ProductStatus
  /** Metadata-driven custom fields (JSONB — tenant configurable) */
  metadata?: Record<string, unknown>
  notes?: string
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const ProductUnitSchema = z.enum([
  'piece', 'kilogram', 'gram', 'liter', 'milliliter',
  'meter', 'box', 'pack', 'set', 'pair', 'bottle', 'can',
])

export const CreatePOSProductSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  sku: z.string().min(1).max(50),
  barcode: z.string().max(50).optional(),
  name: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  categoryId: z.string().optional(),
  categoryName: z.string().max(100).optional(),
  unit: ProductUnitSchema.default('piece'),
  retailPrice: z.number().min(0),
  wholesalePrice: z.number().min(0).optional(),
  wholesaleMinQty: z.number().int().min(1).optional(),
  costPrice: z.number().min(0).default(0),
  markupPercent: z.number().min(0).default(0),
  taxType: z.enum(['vat', 'vat_exempt', 'zero_rated']).default('vat'),
  vatRate: z.number().min(0).max(1).default(0.12),
  isVatInclusive: z.boolean().default(true),
  stockOnHand: z.number().min(0).default(0),
  minStockLevel: z.number().min(0).default(5),
  maxStockLevel: z.number().min(0).default(100),
  supplierId: z.string().optional(),
  supplierName: z.string().max(200).optional(),
  imageUrl: z.string().max(500).optional(),
  isSellable: z.boolean().default(true),
  allowDiscount: z.boolean().default(true),
  sortOrder: z.number().int().min(0).default(0),
  status: z.enum(['active', 'inactive', 'discontinued']).default('active'),
  metadata: z.record(z.unknown()).optional(),
  notes: notesSchema,
})

export const UpdatePOSProductSchema = createUpdateSchema({
  barcode: z.string().max(50).optional(),
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(500).optional(),
  categoryId: z.string().nullable().optional(),
  categoryName: z.string().max(100).optional(),
  unit: ProductUnitSchema.optional(),
  retailPrice: z.number().min(0).optional(),
  wholesalePrice: z.number().min(0).nullable().optional(),
  wholesaleMinQty: z.number().int().min(1).nullable().optional(),
  costPrice: z.number().min(0).optional(),
  markupPercent: z.number().min(0).optional(),
  taxType: z.enum(['vat', 'vat_exempt', 'zero_rated']).optional(),
  vatRate: z.number().min(0).max(1).optional(),
  isVatInclusive: z.boolean().optional(),
  stockOnHand: z.number().min(0).optional(),
  minStockLevel: z.number().min(0).optional(),
  maxStockLevel: z.number().min(0).optional(),
  supplierId: z.string().nullable().optional(),
  supplierName: z.string().max(200).optional(),
  imageUrl: z.string().max(500).nullable().optional(),
  isSellable: z.boolean().optional(),
  allowDiscount: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  status: z.enum(['active', 'inactive', 'discontinued']).optional(),
  metadata: z.record(z.unknown()).optional(),
  notes: notesSchema.optional(),
})

export const POSProductQuerySchema = createQuerySchema({
  categoryId: z.string().optional(),
  status: z.enum(['active', 'inactive', 'discontinued']).optional(),
  isSellable: z.boolean().optional(),
})

export const PRODUCT_UNIT_LABELS: Record<ProductUnit, string> = {
  piece: 'Piece', kilogram: 'Kilogram', gram: 'Gram', liter: 'Liter',
  milliliter: 'Milliliter', meter: 'Meter', box: 'Box', pack: 'Pack',
  set: 'Set', pair: 'Pair', bottle: 'Bottle', can: 'Can',
}

export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  active: 'Active', inactive: 'Inactive', discontinued: 'Discontinued',
}

export const PRODUCT_STATUS_COLORS: Record<ProductStatus, 'green' | 'gray' | 'red'> = {
  active: 'green', inactive: 'gray', discontinued: 'red',
}

export const TAX_TYPE_LABELS: Record<TaxType, string> = {
  vat: 'VAT', vat_exempt: 'VAT Exempt', zero_rated: 'Zero-Rated',
}

// ═══════════════════════════════════════════════════════════════
// POSSaleItem — line item in a sale
// ═══════════════════════════════════════════════════════════════

export interface POSSaleItem {
  id: string
  tenantId: string
  saleId: string
  productId: string
  sku: string
  barcode?: string
  name: string
  unit: ProductUnit
  quantity: number
  unitPrice: number
  discountPercent: number
  discountAmount: number
  lineTotal: number
  vatAmount: number
  vatRate: number
  isVatInclusive: boolean
  costPrice: number
  /** Line-level notes (e.g. gift wrap, special request) */
  notes?: string
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const CreatePOSSaleItemSchema = z.object({
  tenantId: z.string().min(1),
  saleId: z.string().min(1),
  productId: z.string().min(1),
  sku: z.string().min(1),
  barcode: z.string().optional(),
  name: z.string().min(1),
  unit: ProductUnitSchema,
  quantity: z.number().min(0.001),
  unitPrice: z.number().min(0),
  discountPercent: z.number().min(0).max(100).default(0),
  discountAmount: z.number().min(0).default(0),
  lineTotal: z.number().min(0),
  vatAmount: z.number().min(0).default(0),
  vatRate: z.number().min(0).default(0.12),
  isVatInclusive: z.boolean().default(true),
  costPrice: z.number().min(0).default(0),
  notes: z.string().max(200).optional(),
})

// ═══════════════════════════════════════════════════════════════
// POSSale — sale transaction (aggregate root)
// ═══════════════════════════════════════════════════════════════

export type PaymentMethod = 'cash' | 'gcash' | 'maya' | 'bank_transfer' | 'card_debit' | 'card_credit' | 'cheque' | 'split'
export type SaleStatus = 'completed' | 'voided' | 'refunded' | 'pending'

export interface POSSale {
  id: string
  tenantId: string
  branchId?: string
  /** Shift this sale belongs to */
  shiftId?: string
  /** Sequential sale number for the day (e.g. INV-20251215-0042) */
  invoiceNumber: string
  /** Sale date (ISO YYYY-MM-DD) */
  saleDate: string
  /** Sale timestamp */
  saleTime: string
  /** Customer reference (optional — walk-in if empty) */
  customerId?: string
  customerName?: string
  /** Line items */
  items: POSSaleItem[]
  /** Number of items */
  itemCount: number
  /** Subtotal before discounts and tax */
  subtotal: number
  /** Total discount (sale-level + line-level) */
  discountTotal: number
  /** Sale-level discount percent */
  discountPercent: number
  /** Sale-level discount label (e.g. "Senior Citizen", "PWD", "Promo") */
  discountLabel?: string
  /** VAT total */
  vatTotal: number
  /** VAT-exempt sale amount (senior/PWD) */
  vatExemptAmount: number
  /** Grand total */
  grandTotal: number
  /** Amount tendered / paid */
  amountTendered: number
  /** Change due */
  changeDue: number
  /** Payment method */
  paymentMethod: PaymentMethod
  /** For split payments: breakdown of methods and amounts */
  paymentBreakdown?: { method: PaymentMethod; amount: number; reference?: string }[]
  /** Payment reference (GCash ref#, card auth code, etc.) */
  paymentReference?: string
  /** Status */
  status: SaleStatus
  /** Who rang up the sale */
  cashierId: string
  cashierName: string
  /** Void/refund reason (if applicable) */
  voidReason?: string
  /** Void/refund timestamp */
  voidedAt?: number
  voidedBy?: string
  /** Whether this was synced from offline */
  isOffline: boolean
  /** Original sync timestamp */
  syncedAt?: number
  notes?: string
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const PaymentMethodSchema = z.enum([
  'cash', 'gcash', 'maya', 'bank_transfer', 'card_debit',
  'card_credit', 'cheque', 'split',
])

export const PaymentBreakdownSchema = z.object({
  method: PaymentMethodSchema,
  amount: z.number().min(0),
  reference: z.string().max(100).optional(),
})

export const CreatePOSSaleSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  shiftId: z.string().optional(),
  invoiceNumber: z.string().min(1).max(30),
  saleDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  saleTime: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  customerId: z.string().optional(),
  customerName: z.string().max(200).optional(),
  items: z.array(CreatePOSSaleItemSchema).min(1),
  itemCount: z.number().int().min(1),
  subtotal: z.number().min(0),
  discountTotal: z.number().min(0).default(0),
  discountPercent: z.number().min(0).max(100).default(0),
  discountLabel: z.string().max(100).optional(),
  vatTotal: z.number().min(0).default(0),
  vatExemptAmount: z.number().min(0).default(0),
  grandTotal: z.number().min(0),
  amountTendered: z.number().min(0).default(0),
  changeDue: z.number().min(0).default(0),
  paymentMethod: PaymentMethodSchema,
  paymentBreakdown: z.array(PaymentBreakdownSchema).optional(),
  paymentReference: z.string().max(200).optional(),
  status: z.enum(['completed', 'voided', 'refunded', 'pending']).default('completed'),
  cashierId: z.string().min(1),
  cashierName: z.string().min(1),
  voidReason: z.string().max(500).optional(),
  voidedAt: z.number().optional(),
  voidedBy: z.string().optional(),
  isOffline: z.boolean().default(false),
  syncedAt: z.number().optional(),
  notes: notesSchema,
})

export const UpdatePOSSaleSchema = createUpdateSchema({
  customerId: z.string().nullable().optional(),
  customerName: z.string().max(200).nullable().optional(),
  status: z.enum(['completed', 'voided', 'refunded', 'pending']).optional(),
  voidReason: z.string().max(500).optional(),
  voidedAt: z.number().optional(),
  voidedBy: z.string().optional(),
  notes: notesSchema.optional(),
})

export const POSSaleQuerySchema = createQuerySchema({
  saleDate: z.string().optional(),
  status: z.enum(['completed', 'voided', 'refunded', 'pending']).optional(),
  paymentMethod: PaymentMethodSchema.optional(),
  cashierId: z.string().optional(),
  branchId: z.string().optional(),
  shiftId: z.string().optional(),
  customerId: z.string().optional(),
})

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  gcash: 'GCash',
  maya: 'Maya',
  bank_transfer: 'Bank Transfer',
  card_debit: 'Debit Card',
  card_credit: 'Credit Card',
  cheque: 'Cheque',
  split: 'Split Payment',
}

export const PAYMENT_METHOD_COLORS: Record<PaymentMethod, string> = {
  cash: 'green',
  gcash: 'blue',
  maya: 'purple',
  bank_transfer: 'gray',
  card_debit: 'yellow',
  card_credit: 'orange',
  cheque: 'red',
  split: 'gray',
}

export const SALE_STATUS_LABELS: Record<SaleStatus, string> = {
  completed: 'Completed',
  voided: 'Voided',
  refunded: 'Refunded',
  pending: 'Pending',
}

export const SALE_STATUS_COLORS: Record<SaleStatus, 'green' | 'red' | 'yellow' | 'gray'> = {
  completed: 'green',
  voided: 'red',
  refunded: 'yellow',
  pending: 'gray',
}

// ═══════════════════════════════════════════════════════════════
// POSShift — cash register shift
// ═══════════════════════════════════════════════════════════════

export type ShiftStatus = 'open' | 'closed' | 'reconciled'

export interface POSShift {
  id: string
  tenantId: string
  branchId?: string
  /** Shift sequential number */
  shiftNumber: string
  /** Date of shift (ISO date) */
  shiftDate: string
  /** Time shift opened */
  openTime: string
  /** Time shift closed */
  closeTime?: string
  /** Cashier who opened */
  openedBy: string
  openedByName: string
  /** Cashier who closed */
  closedBy?: string
  closedByName?: string
  /** Opening cash float (PHP) */
  openingCash: number
  /** Expected closing cash = opening + cash sales - payouts */
  expectedCash: number
  /** Actual cash counted at close */
  actualCash?: number
  /** Cash discrepancy */
  cashDiscrepancy?: number
  /** Total sales in this shift */
  totalSales: number
  /** Number of transactions */
  transactionCount: number
  /** Breakdown by payment method */
  paymentBreakdown?: Record<string, number>
  /** Cash payouts / expenses during shift */
  payouts: { reason: string; amount: number; timestamp: number }[]
  /** Total payouts */
  totalPayouts: number
  /** Void count */
  voidCount: number
  /** Refund count */
  refundCount: number
  /** Status */
  status: ShiftStatus
  notes?: string
  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const CreatePOSShiftSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  shiftNumber: z.string().min(1).max(20),
  shiftDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  openTime: z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/),
  openedBy: z.string().min(1),
  openedByName: z.string().min(1),
  openingCash: z.number().min(0).default(0),
  expectedCash: z.number().min(0).default(0),
  totalSales: z.number().min(0).default(0),
  transactionCount: z.number().int().min(0).default(0),
  paymentBreakdown: z.record(z.number()).optional(),
  payouts: z.array(z.object({
    reason: z.string().min(1),
    amount: z.number().min(0),
    timestamp: z.number(),
  })).default([]),
  totalPayouts: z.number().min(0).default(0),
  voidCount: z.number().int().min(0).default(0),
  refundCount: z.number().int().min(0).default(0),
  status: z.enum(['open', 'closed', 'reconciled']).default('open'),
  notes: notesSchema,
})

export const UpdatePOSShiftSchema = createUpdateSchema({
  closeTime: z.string().optional(),
  closedBy: z.string().optional(),
  closedByName: z.string().optional(),
  expectedCash: z.number().min(0).optional(),
  actualCash: z.number().min(0).optional(),
  cashDiscrepancy: z.number().optional(),
  totalSales: z.number().min(0).optional(),
  transactionCount: z.number().int().min(0).optional(),
  paymentBreakdown: z.record(z.number()).optional(),
  payouts: z.array(z.object({
    reason: z.string().min(1),
    amount: z.number().min(0),
    timestamp: z.number(),
  })).optional(),
  totalPayouts: z.number().min(0).optional(),
  voidCount: z.number().int().min(0).optional(),
  refundCount: z.number().int().min(0).optional(),
  status: z.enum(['open', 'closed', 'reconciled']).optional(),
  notes: notesSchema.optional(),
})

export const POSShiftQuerySchema = createQuerySchema({
  shiftDate: z.string().optional(),
  status: z.enum(['open', 'closed', 'reconciled']).optional(),
  openedBy: z.string().optional(),
  branchId: z.string().optional(),
})

export const SHIFT_STATUS_LABELS: Record<ShiftStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  reconciled: 'Reconciled',
}

export const SHIFT_STATUS_COLORS: Record<ShiftStatus, 'green' | 'red' | 'blue'> = {
  open: 'green',
  closed: 'red',
  reconciled: 'blue',
}
